import { Box, Button, Paper, Typography } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import CampaignIcon from "@mui/icons-material/Campaign";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import { keyframes } from "@mui/system";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255,165,0,.55); transform: scale(1); }
  60% { box-shadow: 0 0 0 12px rgba(255,165,0,0); transform: scale(1.035); }
  100% { box-shadow: 0 0 0 0 rgba(255,165,0,0); transform: scale(1); }
`;

const Branches = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [loading, setLoading] = useState(true);
  const [adjustments, setAdjustments] = useState([]);

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    Swal.fire({
      customClass: { container: "my-swal" },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteBranch(deletedRow.branchId)
          .then(() => {
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: { container: "my-swal" },
              title: "ลบแล้ว!",
              text: deletedRow.name + " ถูกลบแล้ว",
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            setRows((prevRows) =>
              prevRows
                .filter((row) => row.branchId !== deletedRow.branchId)
                .map((item, index) => ({ ...item, id: index + 1 }))
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting branch:", error);
          });
      }
    });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const payload = { name: newRow.name };

    services
      .updateBranch(newRow.branchId, payload)
      .then(() => {
        toast.success("แก้ไขสำเร็จ!");
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
      })
      .catch((error) => {
        toast.error("แก้ไขไม่สำเร็จ!");
        console.error("Error updating branch:", error);
      });

    const originalRow = rows.find((row) => row.id === newRow.id);
    setRowModesModel({
      ...rowModesModel,
      [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    return originalRow;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const shopResponse = await services.getShop();
      const shop = shopResponse.data.data;

      setAdjustments(shop?.adjustments ?? []);

      const shopsWithIndex = (shop?.branches ?? []).map((item, index) => ({
        ...item,
        id: index + 1,
        branchId: item.id,
        count: item._count?.users ?? 0,
        needsAnnounceUpdate: item.needsAnnounceUpdate || false,
      }));

      setRows(shopsWithIndex);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ลำดับ", flex: 0, width: 70 },
      { field: "name", headerName: "ชื่อสาขา", flex: 1, editable: true },
      {
        field: "description",
        headerName: "คำอธิบาย",
        flex: 1,
        editable: false,
      },
      { field: "count", headerName: "จำนวนสมาชิก", flex: 1, editable: false },
      {
        field: "publish",
        headerName: "",
        width: 170,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { branchId, needsAnnounceUpdate } = params.row;
          const hasAdjustForThisBranch = adjustments?.some(
            (a) => a.branchId === branchId
          );

          if (hasAdjustForThisBranch || needsAnnounceUpdate)
            return (
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                onClick={async () => {
                  Swal.fire({
                    title: "ยืนยันประกาศราคา?",
                    text: "คุณแน่ใจหรือไม่ที่จะประกาศราคาสำหรับสาขานี้",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#f59e0b",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "ใช่, ประกาศเลย!",
                    cancelButtonText: "ยกเลิก",
                    customClass: { container: "my-swal" },
                  }).then(async (result) => {
                    if (result.isConfirmed) {
                      try {
                        // ถ้า needsAnnounceUpdate ให้ patch ก่อน
                        if (needsAnnounceUpdate) {
                          await services.patchBranchAnnounce(branchId);
                          toast.success("อัปเดตประกาศปรับราคาหลักแล้ว!");
                        }

                        if (hasAdjustForThisBranch) {
                          // ประกาศราคาปรับ
                          await services.publishProductAdjustments(branchId);
                          toast.success("อัปเดตประกาศปรับราคาย่อยแล้ว!");
                        }

                        Swal.fire({
                          icon: "success",
                          title: "ประกาศสำเร็จ!",
                          text: "อัปเดตราคาสำหรับสาขานี้แล้ว",
                          confirmButtonText: "ตกลง",
                          customClass: { container: "my-swal" },
                        });

                        await fetchData();
                      } catch (e) {
                        Swal.fire({
                          icon: "error",
                          title: "เกิดข้อผิดพลาด!",
                          text: "ไม่สามารถประกาศราคาได้",
                          confirmButtonText: "กลับ",
                          customClass: { container: "my-swal" },
                        });
                        console.error(e);
                      }
                    }
                  });
                }}
                sx={{
                  bgcolor: "warning.main",
                  color: "warning.contrastText",
                  borderColor: "warning.dark",
                  animation: `${pulse} 1.6s infinite ease-in-out`,
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2,
                  "&:hover": { bgcolor: "warning.dark" },
                }}
              >
                อัปเดตประกาศ
              </Button>
            );
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 0,
        headerAlign: "center",
        align: "center",
        renderCell: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                key={`${id}-save`}
                icon={<SaveIcon />}
                label="Save"
                sx={{ color: "primary.main" }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                key={`${id}-cancel`}
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              key={`${id}-edit`}
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              key={`${id}-delete`}
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        },
      },
      {
        field: "pricing",
        headerName: "",
        width: 150,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            onClick={() => navigate(`/branches/pricing/${params.row.branchId}`)}
          >
            จัดการราคา
          </Button>
        ),
      },
    ],
    [adjustments, rowModesModel]
  );

  return (
    <div>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          รายการสาขา
        </Typography>
        <Button
          color="success"
          variant="contained"
          onClick={() => navigate("/branches/add")}
        >
          เพิ่มสาขา
        </Button>
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              loading={loading}
              slotProps={{
                toolbar: { setRows, setRowModesModel },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{ pagination: { page: 0, pageSize: 50 } }}
              pageSizeOptions={[25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>
    </div>
  );
};

export default Branches;
